import { PDFDocument } from "pdf-lib";
import { normalizeImageFile, placeImage, MARGIN_PT, PRESET_PT, ImageReadError } from "./imagePdf";

/** Picture types Merge PDF turns into a page. Everything is read and drawn in the browser. */
export const MERGE_IMAGE_ACCEPT = ".jpg,.jpeg,.png";

export function isMergeImage(f: { name: string; type: string }): boolean {
  return /^image\/(jpeg|png)$/.test(f.type) || /\.(jpe?g|png)$/i.test(f.name);
}

const isPng = (b: Uint8Array) => b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47;

/**
 * One JPG or PNG as a one-page PDF file, so it flows through the same list, page picker and merge as any PDF.
 * The page is US Letter (the site's default paper), turned landscape for a wide picture, with a small margin;
 * the picture is shown whole and never enlarged. The picture bytes are embedded as they are (no re-compression).
 * The file is named after the picture, with .pdf, and keeps its modified time so it keeps its identity in the list.
 */
export async function imageToPdfFile(file: File): Promise<File> {
  const norm = await normalizeImageFile(file); // upright JPEG/PNG, or throws ImageReadError with a plain reason
  const bytes = new Uint8Array(await norm.arrayBuffer());
  const doc = await PDFDocument.create();
  let img;
  try {
    img = isPng(bytes) ? await doc.embedPng(bytes) : await doc.embedJpg(bytes);
  } catch {
    throw new ImageReadError("could not be read");
  }
  const landscape = img.width > img.height;
  const w = landscape ? PRESET_PT.letter.h : PRESET_PT.letter.w;
  const h = landscape ? PRESET_PT.letter.w : PRESET_PT.letter.h;
  const m = MARGIN_PT.small;
  const p = placeImage({ x: m, y: m, w: w - m * 2, h: h - m * 2 }, img.width, img.height, "fit");
  doc.addPage([w, h]).drawImage(img, { x: p.x, y: p.y, width: p.w, height: p.h });
  const stem = file.name.replace(/\.[^.]+$/, "") || "image";
  return new File([new Uint8Array(await doc.save())], `${stem}.pdf`, { type: "application/pdf", lastModified: file.lastModified });
}
